import classNames from 'classnames'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  clearReportCargoTabPatch,
  fetchPatchCargoStatus,
  setCargoModalReportTabConstraints,
  setCurrentCargo,
  setCurrentCargoGroupIdClient,
  setIsOnlyCargo,
  setIsVisibleModalCargo,
  setNewCargoReportIdPrefill,
  setReportCargoDeleteDialog,
  type Cargo,
} from '../../../../redux/reducers/cargos'
import {
  fetchReportCargosClientOrder,
  fetchReportGroupedCargos,
} from '../../../../redux/reducers/reports'
import { cargosSelector } from '../../../../redux/selectors/cargos-selector'
import { reportsSelector } from '../../../../redux/selectors/reports-selector'
import type { AppDispatch } from '../../../../redux/store'
import type {
  ReportCargoDirection,
  ReportCargoListKind,
  ReportCargoStatus,
  ReportGroupedCargoItem,
} from '../../../../redux/types/reportGroupedCargos'
import { canCurrentUserDeleteCargo } from '../../../../utils/cargoDeletePermission'
import { getRole } from '../../../../utils/getRole'
import styles from '../ReportModal.module.css'
import type { PickupSenderDef, ShipmentRow } from './cargoTypes'
import {
  buildSendersFromGroupedRows,
  buildUnloadingSendersFromGrouped,
  clientIdsForPersistOrder,
  isUnloadingNoRecipientSenderRowKey,
  reorderSendersWithCompletedAtBottom,
  resolveShowDeleteForShipment,
  sendersRowOrderChanged,
  type ShowDeleteForShipment,
} from './groupedCargosTransform'
import PickupSenderCard from './PickupSenderCard'
import PickupSendersSortable from './PickupSendersSortable'

function filterCargosByReportId(
  list: Cargo[],
  reportId: number | null
): Cargo[] {
  if (reportId == null || Number.isNaN(reportId)) return list
  return list.filter((c) => c.report_id === reportId)
}

function nextNonDraftClientStoreKeyAfterDraft(
  prev: PickupSenderDef[],
  draftIndex: number
): string | null {
  for (let i = draftIndex + 1; i < prev.length; i++) {
    const p = prev[i]
    if (p.rowKey.startsWith('pickup-added-')) continue
    return p.clientStoreKey ?? null
  }
  return null
}

function mergePickupSendersWithGroupedApiOrder(
  prev: PickupSenderDef[],
  api: PickupSenderDef[]
): PickupSenderDef[] {
  const draftsBeforeKey = new Map<string | null, PickupSenderDef[]>()

  for (let i = 0; i < prev.length; i++) {
    const p = prev[i]
    if (!p.rowKey.startsWith('pickup-added-')) continue
    const nextKey = nextNonDraftClientStoreKeyAfterDraft(prev, i)
    const bucket = draftsBeforeKey.get(nextKey) ?? []
    bucket.push(p)
    draftsBeforeKey.set(nextKey, bucket)
  }

  const nextSenders: PickupSenderDef[] = []

  for (const row of api) {
    const k = row.clientStoreKey ?? null
    const pre = draftsBeforeKey.get(k)
    if (pre?.length) {
      nextSenders.push(...pre)
      draftsBeforeKey.delete(k)
    }
    nextSenders.push(row)
  }

  draftsBeforeKey.forEach((tail) => {
    nextSenders.push(...tail)
  })

  return nextSenders
}

type PatchChecklistResult = {
  allSucceeded: boolean
  succeededShipmentKeys: string[]
}

/** После массового чекбокса клиента: частичный успех при «включить все» не помечает непропатченные грузы — карточка не уходит вниз, пока не все доставлены */
function mergeBulkSenderCheckboxState(
  prev: Record<string, boolean>,
  senderKey: string,
  shipmentIds: string[],
  checked: boolean,
  patchResult: PatchChecklistResult
): Record<string, boolean> {
  const next = { ...prev }
  const succeeded = new Set(patchResult.succeededShipmentKeys)

  if (checked) {
    shipmentIds.forEach((id) => {
      next[id] = Boolean(prev[id]) || succeeded.has(id)
    })
  } else {
    const idsToSetFalse = patchResult.allSucceeded
      ? shipmentIds
      : patchResult.succeededShipmentKeys
    idsToSetFalse.forEach((id) => {
      next[id] = false
    })
  }

  next[senderKey] = shipmentIds.every((sid) => Boolean(next[sid]))
  return next
}

function isShipmentCheckedByStatus(
  mode: 'pickup' | 'delivered',
  status: ReportCargoStatus | undefined
): boolean {
  if (mode === 'pickup') {
    return status === 'loaded' || status === 'delivered'
  }
  return status === 'delivered'
}

function buildCheckedMapFromSenders(
  mode: 'pickup' | 'delivered',
  senders: PickupSenderDef[]
): Record<string, boolean> {
  const next: Record<string, boolean> = {}
  senders.forEach((sender) => {
    let allChecked = sender.shipments.length > 0
    sender.shipments.forEach((shipment) => {
      const checked = isShipmentCheckedByStatus(
        mode,
        shipment.sourceCargo?.status
      )
      next[shipment.id] = checked
      if (!checked) allChecked = false
    })
    next[sender.rowKey] = allChecked
  })
  return next
}

function removeCargoFromSenders(
  senders: PickupSenderDef[],
  cargoId: number
): PickupSenderDef[] {
  return senders
    .map((sender) => {
      const shipments = sender.shipments.filter((s) => s.id !== String(cargoId))
      return {
        ...sender,
        shipments,
        count: shipments.length,
      }
    })
    .filter((sender) => sender.shipments.length > 0)
}

function removeCargoFromOptionalSender(
  sender: PickupSenderDef | null,
  cargoId: number
): PickupSenderDef | null {
  if (!sender) return null
  const shipments = sender.shipments.filter((s) => s.id !== String(cargoId))
  if (shipments.length === 0) return null
  return {
    ...sender,
    shipments,
    count: shipments.length,
  }
}

const MODE_HINT_OFFSET = { x: 16, y: 22 }

const TONNES_FORMATTER = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

function formatKgToTonnes(weightInKg: number): string {
  return TONNES_FORMATTER.format(weightInKg / 1000)
}

function cargoRowsFromList(
  list: Cargo[],
  showDelete: ShowDeleteForShipment
): ShipmentRow[] {
  return list.map((c) => ({
    id: String(c.id),
    title: c.description?.trim() ? c.description : `Груз №${c.id}`,
    colMain: 'col col-sm-10 pl-1',
    colRight: 'col-auto col-sm-2 pl-1 pr-0',
    showDelete: resolveShowDeleteForShipment(
      showDelete,
      c as unknown as ReportGroupedCargoItem
    ),
    showPhoto: (c.images?.length ?? 0) > 0,
    photoCount: c.images?.length,
    sourceCargo: c,
  }))
}

/** Грузы путевого для клиента: в store группировка по плательщику, для числового id фильтруем по from/to/third. */
function buildShipmentsFromCargosStore(
  cargos: Record<string, Cargo[]> | null | undefined,
  selectClientValue: string,
  reportId: number | null,
  showDelete: ShowDeleteForShipment
): ShipmentRow[] {
  if (!cargos) return []
  if (reportId == null || Number.isNaN(reportId)) return []

  const flatAll = (): Cargo[] => Object.values(cargos).flat()

  let candidates: Cargo[]
  if (selectClientValue === 'client') {
    candidates = flatAll()
  } else if (selectClientValue === 'other') {
    candidates = cargos[''] ?? []
  } else {
    const cid = Number(selectClientValue)
    if (!Number.isFinite(cid)) {
      candidates = cargos[selectClientValue] ?? []
    } else {
      candidates = flatAll().filter(
        (c) => c.from_id === cid || c.to_id === cid || c.third_id === cid
      )
    }
  }

  if (!candidates.length) return []
  const list = filterCargosByReportId(candidates, reportId)
  if (!list.length) return []
  return cargoRowsFromList(list, showDelete)
}

type CargosToProps = {
  direction?: ReportCargoDirection
  /** Водитель: отправленный путевой из раздела «Путевые» — без изменений грузов */
  readOnly?: boolean
}

const CargosTo: FC<CargosToProps> = ({
  direction = 'forward',
  readOnly = false,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { isDriver, userId } = getRole()
  const { cargos, isPendingGetCargos, reportCargoTabPatch } =
    useSelector(cargosSelector)
  const {
    currentReport,
    currentReportId,
    groupedCargosForward,
    groupedCargosBackward,
    isPendingGroupedCargos,
    isPendingReportCargosClientOrder,
  } = useSelector(reportsSelector)

  const reportAssignedUserId = currentReport?.user?.id

  const shipmentDeleteVisibility: ShowDeleteForShipment = useMemo(() => {
    if (readOnly) return false
    const ctx = {
      reportAssignedUserId:
        reportAssignedUserId != null ? Number(reportAssignedUserId) : null,
    }
    return (item: ReportGroupedCargoItem) =>
      !item.invoice_id &&
      canCurrentUserDeleteCargo(
        item as unknown as {
          created_by?: number | null
          report?: { user?: { id?: number | null } }
        },
        ctx
      )
  }, [readOnly, reportAssignedUserId])

  const hideWholeDeliveryDelete = useMemo(
    () =>
      readOnly ||
      (isDriver &&
        (userId == null ||
          reportAssignedUserId == null ||
          Number(reportAssignedUserId) !== Number(userId))),
    [readOnly, isDriver, userId, reportAssignedUserId]
  )

  const groupedFetchSawPendingRef = useRef(false)
  const groupedFetchInitialIdleDoneRef = useRef(false)

  useEffect(() => {
    if (isPendingGroupedCargos) {
      groupedFetchSawPendingRef.current = true
    } else if (groupedFetchSawPendingRef.current) {
      groupedFetchInitialIdleDoneRef.current = true
    }
  }, [isPendingGroupedCargos])

  const showCargoListsLoading =
    isPendingGroupedCargos && !groupedFetchInitialIdleDoneRef.current

  const activeReportNumericId = useMemo(() => {
    const rid = currentReport?.id ?? currentReportId
    if (rid == null || rid === '') return null
    const n = Number(rid)
    return Number.isFinite(n) ? n : null
  }, [currentReport?.id, currentReportId])

  const groupedData =
    direction === 'forward' ? groupedCargosForward : groupedCargosBackward

  const vehicleTotals = groupedData?.totals

  const weightBarPct = useMemo(() => {
    if (!vehicleTotals?.weight?.max || vehicleTotals.weight.max <= 0) return 0
    return Math.min(
      100,
      (vehicleTotals.weight.current / vehicleTotals.weight.max) * 100
    )
  }, [vehicleTotals])

  const volumeBarPct = useMemo(() => {
    if (!vehicleTotals?.volume?.max || vehicleTotals.volume.max <= 0) return 0
    return Math.min(
      100,
      (vehicleTotals.volume.current / vehicleTotals.volume.max) * 100
    )
  }, [vehicleTotals])
  const isWeightOverLimit = Boolean(
    vehicleTotals?.weight?.max &&
      vehicleTotals.weight.max > 0 &&
      vehicleTotals.weight.current > vehicleTotals.weight.max
  )
  const isVolumeOverLimit = Boolean(
    vehicleTotals?.volume?.max &&
      vehicleTotals.volume.max > 0 &&
      vehicleTotals.volume.current > vehicleTotals.volume.max
  )

  const [shipmentMode, setShipmentMode] = useState<'pickup' | 'delivered'>(
    'pickup'
  )
  const [openListIds, setOpenListIds] = useState<string[]>([])
  const [openPhoneKey, setOpenPhoneKey] = useState<string | null>(null)

  const [pickupSenders, setPickupSenders] = useState<PickupSenderDef[]>([])
  const [unloadingSenders, setUnloadingSenders] = useState<PickupSenderDef[]>(
    []
  )
  const [unloadingNoRecipientSender, setUnloadingNoRecipientSender] =
    useState<PickupSenderDef | null>(null)

  const [pickupChecked, setPickupChecked] = useState<Record<string, boolean>>(
    {}
  )
  const [deliveredChecked, setDeliveredChecked] = useState<
    Record<string, boolean>
  >({})
  const [pickupPendingBySender, setPickupPendingBySender] = useState<
    Record<string, boolean>
  >({})
  const [deliveredPendingBySender, setDeliveredPendingBySender] = useState<
    Record<string, boolean>
  >({})

  const [modeCursorHint, setModeCursorHint] = useState<{
    text: string
    x: number
    y: number
  } | null>(null)
  const [showModeCursorHint, setShowModeCursorHint] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const apply = () => setShowModeCursorHint(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])
  useEffect(() => {
    if (!showModeCursorHint) setModeCursorHint(null)
  }, [showModeCursorHint])
  const [isChecklistInteractionFlow, setIsChecklistInteractionFlow] =
    useState(false)
  const hasAnyChecklistPending = useMemo(
    () =>
      Object.values(pickupPendingBySender).some(Boolean) ||
      Object.values(deliveredPendingBySender).some(Boolean),
    [pickupPendingBySender, deliveredPendingBySender]
  )
  useEffect(() => {
    if (!isChecklistInteractionFlow) return
    if (
      !hasAnyChecklistPending &&
      !isPendingReportCargosClientOrder &&
      !isPendingGroupedCargos
    ) {
      setIsChecklistInteractionFlow(false)
    }
  }, [
    hasAnyChecklistPending,
    isChecklistInteractionFlow,
    isPendingReportCargosClientOrder,
    isPendingGroupedCargos,
  ])
  const showCargoListsBusyOverlay =
    !showCargoListsLoading && isChecklistInteractionFlow
  useEffect(() => {
    if (!cargos || activeReportNumericId == null) return

    setPickupSenders((prev) =>
      prev.map((sender) => {
        if (
          !sender.rowKey.startsWith('pickup-added-') ||
          !sender.clientStoreKey
        ) {
          return sender
        }
        const shipments = buildShipmentsFromCargosStore(
          cargos,
          sender.clientStoreKey,
          activeReportNumericId,
          shipmentDeleteVisibility
        )
        return {
          ...sender,
          shipments,
          count: shipments.length,
        }
      })
    )
  }, [cargos, activeReportNumericId, shipmentDeleteVisibility])

  useEffect(() => {
    if (activeReportNumericId == null) return
    /* Не подмешивать предыдущий ответ grouped из Redux, пока грузится актуальный */
    if (isPendingGroupedCargos || isPendingReportCargosClientOrder) return
    setPickupSenders((prev) => {
      const api = buildSendersFromGroupedRows(
        groupedData?.loading,
        direction,
        'loading',
        activeReportNumericId,
        shipmentDeleteVisibility
      )
      const nextSenders = mergePickupSendersWithGroupedApiOrder(prev, api)
      setPickupChecked(buildCheckedMapFromSenders('pickup', nextSenders))
      return nextSenders
    })
  }, [
    groupedData?.loading,
    direction,
    activeReportNumericId,
    isPendingGroupedCargos,
    isPendingReportCargosClientOrder,
    shipmentDeleteVisibility,
  ])

  useEffect(() => {
    if (activeReportNumericId == null) return
    if (isPendingGroupedCargos || isPendingReportCargosClientOrder) return
    const { sortableSenders, noRecipientSender } =
      buildUnloadingSendersFromGrouped(
        groupedData?.unloading,
        direction,
        activeReportNumericId,
        false
      )
    setUnloadingSenders(sortableSenders)
    setUnloadingNoRecipientSender(noRecipientSender)
    const forCheckedMap = noRecipientSender
      ? [...sortableSenders, noRecipientSender]
      : sortableSenders
    setDeliveredChecked(buildCheckedMapFromSenders('delivered', forCheckedMap))
  }, [
    groupedData?.unloading,
    direction,
    activeReportNumericId,
    isPendingGroupedCargos,
    isPendingReportCargosClientOrder,
  ])

  const moveModeHint = useCallback(
    (e: React.MouseEvent) => {
      if (!showModeCursorHint) return
      setModeCursorHint((prev) =>
        prev
          ? {
              ...prev,
              x: e.clientX + MODE_HINT_OFFSET.x,
              y: e.clientY + MODE_HINT_OFFSET.y,
            }
          : null
      )
    },
    [showModeCursorHint]
  )

  const leaveModeHint = useCallback(() => setModeCursorHint(null), [])

  const enterPickupHint = useCallback(
    (e: React.MouseEvent) => {
      if (!showModeCursorHint) return
      setModeCursorHint({
        text: 'Загрузка',
        x: e.clientX + MODE_HINT_OFFSET.x,
        y: e.clientY + MODE_HINT_OFFSET.y,
      })
    },
    [showModeCursorHint]
  )

  const enterDeliveredHint = useCallback(
    (e: React.MouseEvent) => {
      if (!showModeCursorHint) return
      setModeCursorHint({
        text: 'Разгрузка',
        x: e.clientX + MODE_HINT_OFFSET.x,
        y: e.clientY + MODE_HINT_OFFSET.y,
      })
    },
    [showModeCursorHint]
  )

  const togglePickupList = useCallback((id: string) => {
    setOpenListIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }, [])

  const persistClientOrder = useCallback(
    (listType: ReportCargoListKind, next: PickupSenderDef[]) => {
      if (activeReportNumericId == null) return
      const client_ids = clientIdsForPersistOrder(next)
      if (!client_ids.length) return

      void dispatch(
        fetchReportCargosClientOrder({
          reportId: activeReportNumericId,
          silentLoadingPage: true,
          body: {
            direction,
            type: listType,
            client_ids,
          },
        })
      ).then((action) => {
        if (fetchReportCargosClientOrder.fulfilled.match(action)) {
          void dispatch(
            fetchReportGroupedCargos({
              reportId: activeReportNumericId,
              direction,
            })
          )
        }
      })
    },
    [activeReportNumericId, direction, dispatch]
  )

  const persistLoadingOrder = useCallback(
    (next: PickupSenderDef[]) => persistClientOrder('loading', next),
    [persistClientOrder]
  )

  const persistUnloadingOrder = useCallback(
    (next: PickupSenderDef[]) => persistClientOrder('unloading', next),
    [persistClientOrder]
  )

  const patchCargosChecklistStatus = useCallback(
    async (
      mode: 'pickup' | 'delivered',
      shipmentKeys: string[],
      checked: boolean
    ): Promise<PatchChecklistResult> => {
      const pairs = shipmentKeys
        .map((key) => ({ key, cargoId: Number(key) }))
        .filter((p) => Number.isFinite(p.cargoId) && p.cargoId > 0)

      if (!pairs.length) {
        return { allSucceeded: true, succeededShipmentKeys: [] }
      }

      const body: { status: ReportCargoStatus; is_active: boolean } = {
        status: mode === 'pickup' ? 'loaded' : 'delivered',
        is_active: checked,
      }

      const results = await Promise.allSettled(
        pairs.map((p) =>
          dispatch(
            fetchPatchCargoStatus({ cargoId: p.cargoId, ...body })
          ).unwrap()
        )
      )

      const succeededShipmentKeys = pairs
        .filter((_, i) => results[i].status === 'fulfilled')
        .map((p) => p.key)

      return {
        allSucceeded: results.every((r) => r.status === 'fulfilled'),
        succeededShipmentKeys,
      }
    },
    [dispatch]
  )

  const setSenderAndShipments = useCallback(
    async (
      mode: 'pickup' | 'delivered',
      senderKey: string,
      shipmentIds: string[],
      checked: boolean
    ) => {
      if (
        mode === 'delivered' &&
        isUnloadingNoRecipientSenderRowKey(senderKey, direction)
      ) {
        return
      }
      const setPending =
        mode === 'pickup'
          ? setPickupPendingBySender
          : setDeliveredPendingBySender
      setIsChecklistInteractionFlow(true)
      setPending((prev) => ({ ...prev, [senderKey]: true }))
      try {
        const checkedMap = mode === 'pickup' ? pickupChecked : deliveredChecked
        const shipmentKeysToPatch = shipmentIds.filter((id) =>
          checked ? !checkedMap[id] : !!checkedMap[id]
        )

        let patchResult: PatchChecklistResult = {
          allSucceeded: true,
          succeededShipmentKeys: [],
        }

        if (shipmentKeysToPatch.length > 0) {
          patchResult = await patchCargosChecklistStatus(
            mode,
            shipmentKeysToPatch,
            checked
          )
          if (patchResult.succeededShipmentKeys.length === 0) return

          if (!patchResult.allSucceeded) {
            toast.warn(
              'Не все грузы удалось обновить. Состояние отмеченных грузов приведено в соответствие с сервером.'
            )
          }
        }

        if (mode === 'pickup') {
          setPickupChecked((prev) => {
            const next = mergeBulkSenderCheckboxState(
              prev,
              senderKey,
              shipmentIds,
              checked,
              patchResult
            )
            setPickupSenders((prevSenders) => {
              const nextSenders = reorderSendersWithCompletedAtBottom(
                prevSenders,
                next
              )
              if (sendersRowOrderChanged(prevSenders, nextSenders)) {
                queueMicrotask(() => persistLoadingOrder(nextSenders))
              } else if (activeReportNumericId != null) {
                queueMicrotask(() => {
                  void dispatch(
                    fetchReportGroupedCargos({
                      reportId: activeReportNumericId,
                      direction,
                    })
                  )
                })
              }
              return nextSenders
            })
            return next
          })
        } else {
          setDeliveredChecked((prev) => {
            const next = mergeBulkSenderCheckboxState(
              prev,
              senderKey,
              shipmentIds,
              checked,
              patchResult
            )
            setUnloadingSenders((prevSenders) => {
              const nextSenders = reorderSendersWithCompletedAtBottom(
                prevSenders,
                next
              )
              if (sendersRowOrderChanged(prevSenders, nextSenders)) {
                queueMicrotask(() => persistUnloadingOrder(nextSenders))
              } else if (activeReportNumericId != null) {
                queueMicrotask(() => {
                  void dispatch(
                    fetchReportGroupedCargos({
                      reportId: activeReportNumericId,
                      direction,
                    })
                  )
                })
              }
              return nextSenders
            })
            return next
          })
        }
      } finally {
        setPending((prev) => ({ ...prev, [senderKey]: false }))
      }
    },
    [
      patchCargosChecklistStatus,
      persistLoadingOrder,
      persistUnloadingOrder,
      activeReportNumericId,
      direction,
      dispatch,
      pickupChecked,
      deliveredChecked,
    ]
  )

  const toggleShipmentOnly = useCallback(
    async (
      mode: 'pickup' | 'delivered',
      id: string,
      checked: boolean,
      parentSenderKey?: string,
      siblingShipmentIds?: string[]
    ) => {
      if (
        mode === 'delivered' &&
        parentSenderKey &&
        isUnloadingNoRecipientSenderRowKey(parentSenderKey, direction)
      ) {
        return
      }
      const pendingKey = parentSenderKey ?? id
      const setPending =
        mode === 'pickup'
          ? setPickupPendingBySender
          : setDeliveredPendingBySender
      setIsChecklistInteractionFlow(true)
      setPending((prev) => ({ ...prev, [pendingKey]: true }))
      try {
        const { succeededShipmentKeys } = await patchCargosChecklistStatus(
          mode,
          [id],
          checked
        )
        if (!succeededShipmentKeys.includes(id)) return

        if (mode === 'pickup') {
          setPickupChecked((prev) => {
            const next = { ...prev, [id]: checked }
            if (
              parentSenderKey &&
              siblingShipmentIds &&
              siblingShipmentIds.length > 0
            ) {
              next[parentSenderKey] = siblingShipmentIds.every((sid) =>
                Boolean(next[sid])
              )
            }
            setPickupSenders((prevSenders) => {
              const nextSenders = reorderSendersWithCompletedAtBottom(
                prevSenders,
                next
              )
              if (sendersRowOrderChanged(prevSenders, nextSenders)) {
                queueMicrotask(() => persistLoadingOrder(nextSenders))
              } else if (activeReportNumericId != null) {
                queueMicrotask(() => {
                  void dispatch(
                    fetchReportGroupedCargos({
                      reportId: activeReportNumericId,
                      direction,
                    })
                  )
                })
              }
              return nextSenders
            })
            return next
          })
        } else {
          setDeliveredChecked((prev) => {
            const next = { ...prev, [id]: checked }
            if (
              parentSenderKey &&
              siblingShipmentIds &&
              siblingShipmentIds.length > 0
            ) {
              next[parentSenderKey] = siblingShipmentIds.every((sid) =>
                Boolean(next[sid])
              )
            }
            setUnloadingSenders((prevSenders) => {
              const nextSenders = reorderSendersWithCompletedAtBottom(
                prevSenders,
                next
              )
              if (sendersRowOrderChanged(prevSenders, nextSenders)) {
                queueMicrotask(() => persistUnloadingOrder(nextSenders))
              } else if (activeReportNumericId != null) {
                queueMicrotask(() => {
                  void dispatch(
                    fetchReportGroupedCargos({
                      reportId: activeReportNumericId,
                      direction,
                    })
                  )
                })
              }
              return nextSenders
            })
            return next
          })
        }
      } finally {
        setPending((prev) => ({ ...prev, [pendingKey]: false }))
      }
    },
    [
      patchCargosChecklistStatus,
      persistLoadingOrder,
      persistUnloadingOrder,
      activeReportNumericId,
      direction,
      dispatch,
    ]
  )

  const setSenderAndShipmentsPickup = useCallback(
    (
      _mode: 'pickup' | 'delivered',
      senderKey: string,
      shipmentIds: string[],
      checked: boolean
    ) => setSenderAndShipments('pickup', senderKey, shipmentIds, checked),
    [setSenderAndShipments]
  )

  const setSenderAndShipmentsDelivered = useCallback(
    (
      _mode: 'pickup' | 'delivered',
      senderKey: string,
      shipmentIds: string[],
      checked: boolean
    ) => setSenderAndShipments('delivered', senderKey, shipmentIds, checked),
    [setSenderAndShipments]
  )

  const toggleShipmentOnlyPickup = useCallback(
    (
      _mode: 'pickup' | 'delivered',
      id: string,
      checked: boolean,
      parentSenderKey?: string,
      siblingShipmentIds?: string[]
    ) =>
      toggleShipmentOnly(
        'pickup',
        id,
        checked,
        parentSenderKey,
        siblingShipmentIds
      ),
    [toggleShipmentOnly]
  )

  const toggleShipmentOnlyDelivered = useCallback(
    (
      _mode: 'pickup' | 'delivered',
      id: string,
      checked: boolean,
      parentSenderKey?: string,
      siblingShipmentIds?: string[]
    ) =>
      toggleShipmentOnly(
        'delivered',
        id,
        checked,
        parentSenderKey,
        siblingShipmentIds
      ),
    [toggleShipmentOnly]
  )

  const removePickupSenderByRowKey = useCallback((rowKey: string) => {
    setPickupSenders((prev) => {
      const sender = prev.find((s) => s.rowKey === rowKey)
      const listId = sender?.listId
      if (listId) {
        setOpenListIds((o) => o.filter((id) => id !== listId))
      }
      return prev.filter((s) => s.rowKey !== rowKey)
    })
  }, [])

  const openReportCargoModal = useCallback(
    ({
      groupClientId,
      listKind,
    }: {
      groupClientId: number | null
      listKind: 'loading' | 'unloading'
    }) => {
      const reportIdVal = currentReport?.id ?? currentReportId
      if (reportIdVal == null || reportIdVal === '') {
        toast.warn('Не выбран путевой лист для груза')
        return
      }
      dispatch(
        setCargoModalReportTabConstraints({
          lockPl: true,
          lockSender: false,
          direction,
          listKind,
        })
      )
      dispatch(setNewCargoReportIdPrefill(Number(reportIdVal)))
      dispatch(setCurrentCargo(null))
      dispatch(setCurrentCargoGroupIdClient(groupClientId))
      dispatch(setIsOnlyCargo(true))
      dispatch(setIsVisibleModalCargo(true))
    },
    [currentReport?.id, currentReportId, direction, dispatch]
  )

  const openNewCargoModalForReportOnly = useCallback(() => {
    openReportCargoModal({
      groupClientId: null,
      listKind: shipmentMode === 'delivered' ? 'unloading' : 'loading',
    })
  }, [openReportCargoModal, shipmentMode])

  const openCargoEditorForShipmentRow = useCallback(
    (row: ShipmentRow) => {
      if (row.sourceCargo) {
        dispatch(
          setCargoModalReportTabConstraints({
            lockPl: true,
            lockSender: false,
            direction,
            listKind: shipmentMode === 'delivered' ? 'unloading' : 'loading',
          })
        )
        dispatch(setCurrentCargo(row.sourceCargo))
        dispatch(setCurrentCargoGroupIdClient(row.sourceCargo.from_id ?? null))
        dispatch(setIsVisibleModalCargo(true))
        dispatch(setIsOnlyCargo(true))
      } else {
        openNewCargoModalForReportOnly()
      }
    },
    [direction, dispatch, openNewCargoModalForReportOnly, shipmentMode]
  )

  const openNewCargoForSenderLoading = useCallback(
    (sender: PickupSenderDef) => {
      openReportCargoModal({
        groupClientId:
          sender.clientStoreKey && sender.clientStoreKey !== 'other'
            ? Number(sender.clientStoreKey)
            : null,
        listKind: 'loading',
      })
    },
    [openReportCargoModal]
  )

  const openNewCargoForSenderUnloading = useCallback(
    (sender: PickupSenderDef) => {
      openReportCargoModal({
        groupClientId:
          sender.clientStoreKey && sender.clientStoreKey !== 'other'
            ? Number(sender.clientStoreKey)
            : null,
        listKind: 'unloading',
      })
    },
    [openReportCargoModal]
  )

  useEffect(() => {
    if (!reportCargoTabPatch) return
    if (reportCargoTabPatch.kind === 'removeCargo') {
      const cargoId = reportCargoTabPatch.cargoId
      setPickupSenders((prev) => removeCargoFromSenders(prev, cargoId))
      setUnloadingSenders((prev) => removeCargoFromSenders(prev, cargoId))
      setUnloadingNoRecipientSender((prev) =>
        removeCargoFromOptionalSender(prev, cargoId)
      )
      setPickupChecked((prev) => {
        const next = { ...prev }
        delete next[String(cargoId)]
        return next
      })
      setDeliveredChecked((prev) => {
        const next = { ...prev }
        delete next[String(cargoId)]
        return next
      })
    } else if (reportCargoTabPatch.kind === 'removeDelivery') {
      removePickupSenderByRowKey(reportCargoTabPatch.senderRowKey)
    }
    dispatch(clearReportCargoTabPatch())
  }, [reportCargoTabPatch, dispatch, removePickupSenderByRowKey])

  return (
    <div
      className={classNames('tab-content', styles.cargosToPanelMinHeight)}
      data-content="report_tab_shipment"
    >
      <div className="row align-items-center mb-1">
        <div className="col-auto col-sm-auto pr-1 pb-2">
          <div
            className={classNames(
              'd-flex flex-wrap align-items-end',
              styles.tabs,
              styles.radioTabs,
              styles.small,
              styles.tabsButtons,
              styles.cargoShipmentModeTabs
            )}
          >
            <div className="mr-1">
              <input
                type="radio"
                name="report_cargo_shipment_mode"
                id="report_tab_shipment_pickup"
                checked={shipmentMode === 'pickup'}
                onChange={() => setShipmentMode('pickup')}
              />
              <label
                className={styles.cargoHint}
                htmlFor="report_tab_shipment_pickup"
                aria-label="Загрузка"
                onMouseEnter={enterPickupHint}
                onMouseMove={moveModeHint}
                onMouseLeave={leaveModeHint}
              >
                <div
                  className={classNames(
                    styles.btn,
                    shipmentMode !== 'pickup' && styles.btnBlueOutline,
                    styles.addCargoBtn
                  )}
                >
                  <i
                    className={classNames(
                      styles.icon,
                      styles.cargoIconTruckLoading,
                      'ml-0 mr-0',
                      shipmentMode === 'pickup' && styles.cargoModeIconOnPrimary
                    )}
                  />
                </div>
              </label>
            </div>
            <div className="mr-1">
              <input
                type="radio"
                name="report_cargo_shipment_mode"
                id="report_tab_shipment_delivered"
                checked={shipmentMode === 'delivered'}
                onChange={() => setShipmentMode('delivered')}
              />
              <label
                className={styles.cargoHint}
                htmlFor="report_tab_shipment_delivered"
                aria-label="Разгрузка"
                onMouseEnter={enterDeliveredHint}
                onMouseMove={moveModeHint}
                onMouseLeave={leaveModeHint}
              >
                <div
                  className={classNames(
                    styles.btn,
                    shipmentMode !== 'delivered' && styles.btnBlueOutline,
                    styles.addCargoBtn
                  )}
                >
                  <i
                    className={classNames(
                      styles.icon,
                      styles.cargoIconTruckDelivery,
                      'ml-0 mr-0',
                      shipmentMode === 'delivered' &&
                        styles.cargoModeIconOnPrimary
                    )}
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
        <div className="col col-sm pl-1 pb-2">
          <div
            className={classNames(
              styles.cargoProgressWrapper,
              // 'mb-1',
              isWeightOverLimit && styles.cargoProgressOverLimit
            )}
          >
            <span
              className={classNames(
                styles.cargoProgressTrack,
                styles.cargoBgTurquoise
              )}
              style={{ width: `${weightBarPct}%` }}
            />
            <span className={styles.cargoProgressText}>
              {vehicleTotals
                ? `${formatKgToTonnes(vehicleTotals.weight.current)} / ${formatKgToTonnes(vehicleTotals.weight.max)} т`
                : '— / — т'}
            </span>
          </div>
          {/* {isWeightOverLimit && (
                        <div className={classNames(styles.cargoProgressWarning, 'mb-1')}>
                            Лимит веса превышен
                        </div>
                    )} */}
          <div
            className={classNames(
              styles.cargoProgressWrapper,
              isVolumeOverLimit && styles.cargoProgressOverLimit
            )}
          >
            <span
              className={classNames(
                styles.cargoProgressTrack,
                styles.cargoBgLightBlue
              )}
              style={{ width: `${volumeBarPct}%` }}
            />
            <span className={styles.cargoProgressText}>
              {vehicleTotals ? (
                <>
                  {vehicleTotals.volume.current} / {vehicleTotals.volume.max} м
                  <sup>3</sup>
                </>
              ) : (
                <>
                  — / — м<sup>3</sup>
                </>
              )}
            </span>
          </div>
          {/* {isVolumeOverLimit && (
                        <div className={styles.cargoProgressWarning}>
                            Лимит объема превышен
                        </div>
                    )} */}
        </div>
        {!readOnly && (
          <div
            className="col-12 col-sm-auto pb-2"
            style={{ textAlign: 'right' }}
          >
            <button
              type="button"
              className={classNames(
                styles.btn,
                styles.btnBlue,
                styles.btnWide,
                styles.addCargoBtn
              )}
              disabled={isPendingGetCargos || isPendingGroupedCargos}
              onClick={openNewCargoModalForReportOnly}
            >
              <b>
                + Груз{' '}
                <i
                  className={classNames(
                    styles.icon,
                    styles.cargoIconBox,
                    styles.cargoIcon20,
                    styles.iconWhite,
                    'ml-1 mr-0'
                  )}
                />
              </b>
            </button>
          </div>
        )}
      </div>

      {showCargoListsLoading ? (
        <div
          className={styles.cargosToListsLoadingPanel}
          role="status"
          aria-live="polite"
        >
          <span className={styles.cargosToInlineSpinner} aria-hidden />
          <span>Загрузка доставок…</span>
        </div>
      ) : (
        <div className={styles.cargosToListsArea}>
          {showCargoListsBusyOverlay && (
            <div
              className={styles.cargosToListsBusyOverlay}
              role="status"
              aria-live="polite"
              aria-label="Обновление доставок"
            >
              <span className={styles.cargosToInlineSpinner} aria-hidden />
              <span className={styles.cargosToListsBusyText}>
                Обновляем доставки...
              </span>
            </div>
          )}
          <div
            className={classNames('tab-content', {
              [styles.cargoTabPanelHidden]: shipmentMode !== 'pickup',
            })}
            data-content="report_tab_shipment_pickup"
          >
            <PickupSendersSortable
              reportCargoDirection={direction}
              readOnly={readOnly}
              canReorder={false}
              senders={pickupSenders}
              onReorder={setPickupSenders}
              onRequestDeleteDelivery={(senderRowKey) => {
                const sender = pickupSenders.find(
                  (s) => s.rowKey === senderRowKey
                )
                const cargoIds =
                  sender?.shipments
                    .map((row) => Number(row.id))
                    .filter((id) => Number.isFinite(id) && id > 0) ?? []
                dispatch(
                  setReportCargoDeleteDialog({
                    type: 'delivery',
                    senderRowKey,
                    cargoIds,
                    direction,
                  })
                )
              }}
              onRequestDeleteShipment={(row) =>
                dispatch(
                  setReportCargoDeleteDialog({
                    type: 'shipment',
                    cargo: row.sourceCargo,
                    direction,
                  })
                )
              }
              onRequestEditShipment={openCargoEditorForShipmentRow}
              onRequestAddShipment={openNewCargoForSenderLoading}
              openListIds={openListIds}
              togglePickupList={togglePickupList}
              pickupChecked={pickupChecked}
              pendingBySender={pickupPendingBySender}
              canAddShipment={!readOnly}
              checklistMode="pickup"
              hideDeliveryDelete={hideWholeDeliveryDelete}
              setSenderAndShipments={setSenderAndShipmentsPickup}
              toggleShipmentOnly={toggleShipmentOnlyPickup}
              openPhoneKey={openPhoneKey}
              setOpenPhoneKey={setOpenPhoneKey}
            />
          </div>
          <div
            className={classNames('tab-content', {
              [styles.cargoTabPanelHidden]: shipmentMode !== 'delivered',
            })}
            data-content="report_tab_shipment_delivered"
          >
            <PickupSendersSortable
              reportCargoDirection={direction}
              readOnly={readOnly}
              senders={unloadingSenders}
              onReorder={setUnloadingSenders}
              onDragEndPersist={persistUnloadingOrder}
              onRequestDeleteDelivery={() => undefined}
              onRequestDeleteShipment={() => undefined}
              onRequestEditShipment={openCargoEditorForShipmentRow}
              onRequestAddShipment={openNewCargoForSenderUnloading}
              openListIds={openListIds}
              togglePickupList={togglePickupList}
              pickupChecked={deliveredChecked}
              pendingBySender={deliveredPendingBySender}
              canAddShipment={!readOnly}
              checklistMode="delivered"
              hideDeliveryDelete
              setSenderAndShipments={setSenderAndShipmentsDelivered}
              toggleShipmentOnly={toggleShipmentOnlyDelivered}
              openPhoneKey={openPhoneKey}
              setOpenPhoneKey={setOpenPhoneKey}
            />
            {unloadingNoRecipientSender && (
              <PickupSenderCard
                reportCargoDirection={direction}
                variant="static"
                sender={unloadingNoRecipientSender}
                listOpen={
                  !!unloadingNoRecipientSender.listId &&
                  openListIds.includes(unloadingNoRecipientSender.listId)
                }
                togglePickupList={togglePickupList}
                pickupChecked={deliveredChecked}
                checkboxDisabled={
                  readOnly ||
                  Boolean(
                    deliveredPendingBySender[unloadingNoRecipientSender.rowKey]
                  )
                }
                setSenderAndShipments={setSenderAndShipmentsDelivered}
                toggleShipmentOnly={toggleShipmentOnlyDelivered}
                openPhoneKey={openPhoneKey}
                setOpenPhoneKey={setOpenPhoneKey}
                onRequestDeleteDelivery={undefined}
                onRequestDeleteShipment={undefined}
                onRequestEditShipment={
                  readOnly ? undefined : openCargoEditorForShipmentRow
                }
                onRequestAddShipment={
                  readOnly ? undefined : openNewCargoForSenderUnloading
                }
                canAddShipment={!readOnly}
                checklistMode="delivered"
                hideDeliveryDelete
              />
            )}
          </div>
        </div>
      )}

      {modeCursorHint &&
        showModeCursorHint &&
        createPortal(
          <div
            className={styles.cargoCursorHint}
            style={{
              left: modeCursorHint.x,
              top: modeCursorHint.y,
            }}
            role="tooltip"
          >
            {modeCursorHint.text}
          </div>,
          document.body
        )}
    </div>
  )
}

export default CargosTo
